// react
import { useCallback, useEffect, useMemo } from 'react';

// components
import useSearchParams from '@/hooks/useStableSearchParams';

import { DateTime } from 'luxon';

const FROM_PARAM = 'from';
const TO_PARAM = 'to';

function createDefaultRange({ zone = 'Europe/Copenhagen', days = 7 } = {}) {
  const nowLocal = DateTime.now().setZone(zone);
  const start = nowLocal.minus({ days }).startOf('day');
  const end = nowLocal.endOf('day');
  return [start, end];
}

function parseRangeFromParams({ fromIso, toIso, zone }) {
  const fromParam = fromIso;
  const toParam = toIso;

  if (!fromParam || !toParam) return null;

  const from = DateTime.fromISO(fromParam).setZone(zone);
  const to = DateTime.fromISO(toParam).setZone(zone);
  if (!from.isValid || !to.isValid) return null;

  return [from, to];
}

export function useUrlDateRange(options = {}) {
  const {
    fromKey = FROM_PARAM,
    toKey = TO_PARAM,
    zone = 'Europe/Copenhagen',
    defaultDays = 30,
    autoSetDefaults = true,
  } = options;

  const [searchParams, setSearchParams] = useSearchParams();
  const fromIso = searchParams.get(fromKey);
  const toIso = searchParams.get(toKey);
  // URL is the source of truth: derive range from URL if valid, else fallback default
  const range = useMemo(() => {
    const parsed = parseRangeFromParams({ fromIso, toIso, zone });
    if (parsed) return parsed;

    // Only create default range if autoSetDefaults is true
    if (autoSetDefaults) {
      return createDefaultRange({ zone, days: defaultDays });
    }

    // Return null range when autoSetDefaults is false and no valid URL params
    return [null, null];
  }, [fromIso, toIso, zone, defaultDays, autoSetDefaults]);

  const [from, to] = range;

  // External sync only: if params are missing/invalid, write defaults to URL (replace: true)
  // This only runs once on mount or when zone/defaultDays/keys change
  useEffect(() => {
    // Only set defaults in URL if autoSetDefaults is true
    if (!autoSetDefaults) return;

    setSearchParams(
      (prev) => {
        const currentFromIso = prev.get(fromKey);
        const currentToIso = prev.get(toKey);

        const parsed = parseRangeFromParams({
          fromIso: currentFromIso,
          toIso: currentToIso,
          zone,
        });
        if (parsed) return prev; // Already valid, nothing to do

        const [start, end] = createDefaultRange({ zone, days: defaultDays });

        const nextFromIso = start.toISO();
        const nextToIso = end.toISO();

        // setSearchParams will handle duplicate prevention
        console.log('useUrlDateRange: Setting default date range in URL.', {
          nextFromIso,
          nextToIso,
        });

        const next = new URLSearchParams(prev);
        next.set(fromKey, nextFromIso);
        next.set(toKey, nextToIso);
        return next;
      },
      { replace: true },
    );
  }, [zone, defaultDays, fromKey, toKey, setSearchParams, autoSetDefaults]);

  // Update function: write to URL; derived values update automatically
  const setRange = useCallback(
    (nextFrom, nextTo, { roundToDay = true, clampToNow = true } = {}) => {
      if (!nextFrom || !nextTo) return;

      let start = nextFrom.setZone(zone);
      let end = nextTo.setZone(zone);

      if (roundToDay) {
        start = start.startOf('day');
        end = end.endOf('day');
      }

      const now = DateTime.now().setZone(zone);
      if (clampToNow && end > now) {
        end = now;
      }

      if (end < start) return;

      const nextFromIso = start.toISO();
      const nextToIso = end.toISO();

      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        const currentFromIso = prev.get(fromKey);
        const currentToIso = prev.get(toKey);

        const same =
          currentFromIso === nextFromIso && currentToIso === nextToIso;
        if (same) return prev; // Return prev instead of undefined to prevent update
        next.set(fromKey, nextFromIso);
        next.set(toKey, nextToIso);
        return next;
      });
    },
    [setSearchParams, fromKey, toKey, zone],
  );
  // Derived values: UTC millis + duration
  const timeRange = useMemo(() => {
    if (!from || !to) return null;
    return {
      start: from.toUTC().toMillis(),
      end: to.toUTC().toMillis(),
    };
  }, [from, to]);

  const durationDays = useMemo(() => {
    if (!from || !to) return null;
    return Math.round(to.diff(from, 'days').days);
  }, [from, to]);
  // console.log('useUrlDateRange from:', from.toISO(), 'to:', to.toISO());
  // console.log('timeRange:', timeRange);
  // console.log('range', range);
  // console.log('searchParams:', Array.from(searchParams.entries()));
  return {
    from,
    to,
    range,
    setRange,
    timeRange,
    durationDays,
  };
}

// import { useCallback, useEffect, useMemo, useState } from 'react';
// import { DateTime } from 'luxon';
// import useSearchParams from '@/hooks/useStableSearchParams';

// export const FROM_PARAM = 'from';
// export const TO_PARAM = 'to';

// function createDefaultRange({ zone = 'Europe/Copenhagen', days = 7 } = {}) {
//   const nowLocal = DateTime.now().setZone(zone);
//   const start = nowLocal.minus({ days }).startOf('day');
//   const end = nowLocal.endOf('day');
//   return [start, end];
// }

// export function useUrlDateRange(options = {}) {
//   const {
//     fromKey = FROM_PARAM,
//     toKey = TO_PARAM,
//     zone = 'Europe/Copenhagen',
//     defaultDays = 30,
//   } = options;

//   const [searchParams, setSearchParams] = useSearchParams();

//   // --- Initial range: try from URL, else fallback ---
//   const [range, setRangeState] = useState(() => {
//     const fromParam = searchParams.get(fromKey);
//     const toParam = searchParams.get(toKey);

//     if (fromParam && toParam) {
//       const from = DateTime.fromISO(fromParam).setZone(zone);
//       const to = DateTime.fromISO(toParam).setZone(zone);

//       if (from.isValid && to.isValid) {
//         return [from, to];
//       }
//     }

//     return createDefaultRange({ zone, days: defaultDays });
//   });

//   // --- Sync with URL changes ---
//   useEffect(() => {
//     const fromParam = searchParams.get(fromKey);
//     const toParam = searchParams.get(toKey);

//     const setDefaultRangeInUrl = () => {
//       const [start, end] = createDefaultRange({ zone, days: defaultDays });
//       setRangeState([start, end]);
//       setSearchParams(
//         (prev) => {
//           const next = new URLSearchParams(prev);
//           next.set(fromKey, start.toISO());
//           next.set(toKey, end.toISO());
//           return next;
//         },
//         { replace: true },
//       );
//     };

//     if (!fromParam || !toParam) {
//       const [currentFrom, currentTo] = range;
//       setSearchParams(
//         (prev) => {
//           const next = new URLSearchParams(prev);
//           next.set(fromKey, currentFrom.toISO());
//           next.set(toKey, currentTo.toISO());
//           return next;
//         },
//         { replace: true },
//       );
//       return;
//     }

//     const from = DateTime.fromISO(fromParam).setZone(zone);
//     const to = DateTime.fromISO(toParam).setZone(zone);

//     if (!from.isValid || !to.isValid) {
//       setDefaultRangeInUrl();
//       return;
//     }

//     setRangeState((prev) => {
//       const [prevFrom, prevTo] = prev || [];
//       const isSame =
//         prevFrom &&
//         prevTo &&
//         prevFrom.toISO() === from.toISO() &&
//         prevTo.toISO() === to.toISO();

//       return isSame ? prev : [from, to];
//     });
//   }, [searchParams, setSearchParams, fromKey, toKey, zone, defaultDays, range]);

//   // --- Function to update range & URL ---
//   const setRange = useCallback(
//     (nextFrom, nextTo, { roundToDay = true, clampToNow = true } = {}) => {
//       if (!nextFrom || !nextTo) return;

//       let start = nextFrom;
//       let end = nextTo;

//       if (roundToDay) {
//         start = start.startOf('day');
//         end = end.endOf('day');
//       }

//       const now = DateTime.now().setZone(zone);
//       if (clampToNow && end > now) {
//         end = now;
//       }

//       if (end < start) return;

//       setRangeState([start, end]);

//       setSearchParams((prev) => {
//         const next = new URLSearchParams(prev);
//         next.set(fromKey, start.toISO());
//         next.set(toKey, end.toISO());
//         return next;
//       });
//     },
//     [setSearchParams, fromKey, toKey, zone],
//   );

//   const [from, to] = range;

//   // --- Derived values: UTC millis + duration ---
//   const timeRange = useMemo(() => {
//     if (!from || !to) return null;
//     return {
//       start: from.toUTC().toMillis(),
//       end: to.toUTC().toMillis(),
//     };
//   }, [from, to]);

//   const durationDays = useMemo(() => {
//     if (!from || !to) return null;
//     return Math.round(to.diff(from, 'days').days);
//   }, [from, to]);

//   // Optionally, you can also expose hours/minutes if useful
//   // const durationHours = useMemo(() => {
//   //   if (!from || !to) return null;
//   //   return Math.round(to.diff(from, 'hours').hours);
//   // }, [from, to]);

//   return {
//     from,
//     to,
//     range, // [from, to]
//     setRange, // (fromDateTime, toDateTime, options?)
//     timeRange, // { start, end } in UTC millis
//     durationDays, // integer (e.g. 7)
//   };
// }
